import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  type DecisionStrategyResult,
  type TwilioVerificationStatus,
  type ValidateOfferCodeResponse,
  BankType,
  CreateApplicationErrors,
  PortalType,
} from '@sparrow-card/sparrow-card-constants';
import { resetState } from 'actions/resetState';
import {
  fetchOfferPrepopulatedData,
  getDocumentVerificationResult,
  submitApplicationData,
  validateOfferCodeData,
} from 'thunks';

const getBankPartnerFromPayload = (payload: unknown): BankType | undefined => {
  if (!payload || typeof payload !== 'object' || !('bankPartner' in payload)) {
    return undefined;
  }

  const bankPartner = (payload as { bankPartner?: unknown }).bankPartner;

  return Object.values(BankType).includes(bankPartner as BankType)
    ? (bankPartner as BankType)
    : undefined;
};

const hasApplicationPayload = (payload: unknown): payload is { application?: { id?: string } } =>
  payload instanceof Object && payload !== null && 'application' in payload;

export enum ApplyState {
  SubmitForm = 'SubmitForm',
  TooManyCanceledApplications = 'TooManyCanceledApplications',
  BorrowerLocked = 'BorrowerLocked',
}

export const getApplyStateFromSubmissionErrors = (
  errors: CreateApplicationErrors[] | undefined,
): ApplyState | undefined => {
  if (!errors?.length) {
    return undefined;
  }

  if (errors.includes(CreateApplicationErrors.TooManyCancelledApplications)) {
    return ApplyState.TooManyCanceledApplications;
  }

  if (errors.includes(CreateApplicationErrors.BorrowerLocked)) {
    return ApplyState.BorrowerLocked;
  }

  return undefined;
};

export interface PhoneVerificationCreateResult {
  status: TwilioVerificationStatus;
  resendAttemptsLeft?: number;
}

export interface ApplicationState {
  applicationId: string | undefined;
  submissionId: string | undefined;
  phoneVerificationCreateResult: PhoneVerificationCreateResult | undefined;
  isMainCheckInProgress: boolean;
  applyState: ApplyState;
  offerCodeState: ValidateOfferCodeResponse | undefined;
  bankPartner: BankType | undefined;
  sessionStartTime: number | undefined;
  portalType: PortalType;
  mainCheckResult: DecisionStrategyResult | undefined;
}

const initialState: ApplicationState = {
  applicationId: undefined,
  submissionId: undefined,
  phoneVerificationCreateResult: undefined,
  isMainCheckInProgress: false,
  applyState: ApplyState.SubmitForm,
  offerCodeState: undefined,
  bankPartner: undefined,
  sessionStartTime: undefined,
  portalType: PortalType.Standard,
  mainCheckResult: undefined,
};

const application = createSlice({
  name: 'application',
  initialState,
  reducers: {
    setApplicationId: (state: ApplicationState, { payload }: PayloadAction<string | undefined>) => {
      state.applicationId = payload;
    },
    setSubmissionId: (state: ApplicationState, { payload }: PayloadAction<string | undefined>) => {
      state.submissionId = payload;
    },
    setPhoneVerificationCreateResult: (
      state: ApplicationState,
      { payload }: PayloadAction<PhoneVerificationCreateResult | undefined>,
    ) => {
      state.phoneVerificationCreateResult = payload;
    },
    setIsMainCheckInProgress: (state: ApplicationState, { payload }: PayloadAction<boolean>) => {
      state.isMainCheckInProgress = payload;
    },
    setStartTime: (state: ApplicationState, { payload }: PayloadAction<number>) => {
      state.sessionStartTime = payload;
    },
    setPortalType: (state: ApplicationState, { payload }: PayloadAction<PortalType>) => {
      state.portalType = payload;
    },
    setApplyState: (state: ApplicationState, { payload }: PayloadAction<ApplyState>) => {
      state.applyState = payload;
    },
    setBankPartner: (state: ApplicationState, { payload }: PayloadAction<BankType | undefined>) => {
      state.bankPartner = payload;
    },
    setMainCheckResult: (
      state: ApplicationState,
      { payload }: PayloadAction<DecisionStrategyResult>,
    ) => {
      state.mainCheckResult = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, (state) => {
      state.applicationId = initialState.applicationId;
      state.submissionId = initialState.submissionId;
      state.phoneVerificationCreateResult = initialState.phoneVerificationCreateResult;
      state.isMainCheckInProgress = initialState.isMainCheckInProgress;
    });
    builder.addCase(submitApplicationData.fulfilled, (state, { payload }) => {
      state.applicationId = hasApplicationPayload(payload)
        ? payload.application?.id
        : state.applicationId;
      state.submissionId =
        'submissionId' in payload && typeof payload.submissionId === 'string'
          ? payload.submissionId
          : state.submissionId;
      state.isMainCheckInProgress = initialState.isMainCheckInProgress;

      const applyStateFromErrors = getApplyStateFromSubmissionErrors(
        'errors' in payload ? payload.errors : undefined,
      );

      if (applyStateFromErrors) {
        state.applyState = applyStateFromErrors;
      }
    });
    builder.addCase(getDocumentVerificationResult.fulfilled, (state, { payload }) => {
      state.applicationId = payload.applicationId ?? undefined;
    });
    builder.addCase(validateOfferCodeData.fulfilled, (state, { payload }) => {
      state.offerCodeState = payload;
      state.bankPartner = getBankPartnerFromPayload(payload) ?? state.bankPartner;
    });
    builder.addCase(fetchOfferPrepopulatedData.fulfilled, (state, { payload }) => {
      state.bankPartner = getBankPartnerFromPayload(payload) ?? state.bankPartner;
    });
  },
});

export const {
  setApplicationId,
  setSubmissionId,
  setPhoneVerificationCreateResult,
  setIsMainCheckInProgress,
  setStartTime,
  setPortalType,
  setApplyState,
  setBankPartner,
  setMainCheckResult,
} = application.actions;

export default application.reducer;
